import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import { initials } from "../../utils/format";
import { EVENT_TYPES, EVENT_TYPE_META, REMINDER_OPTIONS } from "../../utils/constants";
import AssigneePicker from "./AssigneePicker";

const MAX_CHIPS = 4;

const initialForm = ({ type, date, event }) =>
  event
    ? {
        type: event.type,
        title: event.title || "",
        date: event.date || date,
        time: event.time || "",
        location: event.location || "",
        description: event.description || "",
        reminder: event.reminder || "today",
        assignees: (event.assignees || []).map((user) => user.id),
      }
    : { type, title: "", date, time: "10:00", location: "", description: "", reminder: "today", assignees: [] };

/* Create / edit a booking, client meeting or task. Stacks above the calendar and the day details. */
export default function EventModal({ eventForm, onClose, onSaved }) {
  const showToast = useToast();
  const { config } = useAuth();
  const assignmentEnabled = Boolean(config && config.assignment_enabled);
  const editing = eventForm.event;
  const [form, setForm] = useState(() => initialForm(eventForm));
  const [saving, setSaving] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!assignmentEnabled) return undefined;
    let cancelled = false;
    apiClient
      .get("schedule/assignees")
      .then((res) => {
        if (cancelled) return;
        const users = res.data.users || [];
        // still assigned to people who have since lost access: keep them selectable
        const gone = ((editing && editing.assignees) || []).filter((current) => !users.some((u) => u.id === current.id));
        setAssignees([...users, ...gone]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editing, assignmentEnabled]);

  const selectedUsers = useMemo(
    () => form.assignees.map((id) => assignees.find((user) => user.id === id)).filter(Boolean),
    [form.assignees, assignees]
  );

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const focus = (field) => {
    setTimeout(() => {
      const input = document.getElementById(`event-${field}`);
      if (input) input.focus();
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast("Please enter an event title or client name", "error");
      focus("title");
      return;
    }
    if (!form.date) {
      showToast("Please select a date for the schedule", "error");
      focus("date");
      return;
    }

    setSaving(true);
    try {
      const url = editing ? `schedule/update/${editing.id}` : "schedule/add";
      const { assignees: assigneeIds, ...fields } = form;
      const res = await apiClient.post(url, {
        ...fields,
        title: fields.title.trim(),
        time: fields.time || null,
        ...(assignmentEnabled ? { assignees: assigneeIds } : {}),
      });
      showToast(res.data.message);
      onSaved(res.data.event);
    } catch (err) {
      setSaving(false);
      const field = err.errors && Object.keys(err.errors)[0];
      showToast(field ? err.errors[field] : err.message, "error");
      if (field) focus(field.split(".")[0]);
    }
  };

  return (
    <div className="modal-backdrop no-print" style={{ zIndex: 1005 }}>
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h3>{editing ? "Edit Booking / Event" : "Schedule Booking / Event"}</h3>
          <button type="button" className="btn-icon-close" title="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="modal-body">
            <div className="form-group">
              <label>
                Event Category <span className="req">*</span>
              </label>
              <div className="event-type-chips">
                {EVENT_TYPES.map((type) => (
                  <label className="event-type-chip" key={type}>
                    <input type="radio" name="event_type" value={type} checked={form.type === type} onChange={() => update({ type })} />
                    <span className={`chip-label ${EVENT_TYPE_META[type].chipClass}`}>{EVENT_TYPE_META[type].chip}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="event-title">
                Title / Client Name <span className="req">*</span>
              </label>
              <input
                type="text"
                id="event-title"
                className="form-control"
                placeholder="e.g. Signage Installation at Al Barsha"
                maxLength={255}
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
              />
            </div>

            <div className="form-grid grid-2">
              <div className="form-group">
                <label htmlFor="event-date">
                  📅 Date <span className="req">*</span>
                </label>
                <input type="date" id="event-date" className="form-control" value={form.date} onChange={(e) => update({ date: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor="event-time">⏰ Time</label>
                <input type="time" id="event-time" className="form-control" value={form.time} onChange={(e) => update({ time: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="event-location">Meeting Location / Link</label>
              <input
                type="text"
                id="event-location"
                className="form-control"
                placeholder="e.g. Client Office / Google Meet link"
                maxLength={255}
                value={form.location}
                onChange={(e) => update({ location: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-description">Details / Special Notes</label>
              <textarea
                id="event-description"
                className="form-control"
                rows="3"
                placeholder="Additional instructions, artwork requirements, or agenda..."
                maxLength={2000}
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
              ></textarea>
            </div>

            {assignmentEnabled && assignees.length > 0 && (
              <div className="form-group">
                <label htmlFor="event-assignees">👤 Assign To</label>
                <div className="assign-field">
                  {selectedUsers.length === 0 ? (
                    <span className="assign-field-empty">Only me (not assigned)</span>
                  ) : (
                    <div className="assign-chips">
                      {selectedUsers.slice(0, MAX_CHIPS).map((user) => (
                        <span key={user.id} className="assign-chip">
                          <span className="assign-avatar">{initials(user.name)}</span>
                          {user.name}
                          <button
                            type="button"
                            className="assign-chip-remove"
                            title={`Remove ${user.name}`}
                            aria-label={`Remove ${user.name}`}
                            onClick={() => update({ assignees: form.assignees.filter((id) => id !== user.id) })}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      {selectedUsers.length > MAX_CHIPS && <span className="assign-chip assign-chip-more">+{selectedUsers.length - MAX_CHIPS} more</span>}
                    </div>
                  )}
                  <button type="button" id="event-assignees" className="btn btn-secondary btn-sm assign-trigger" onClick={() => setPickerOpen(true)}>
                    {selectedUsers.length ? "Change" : "Select users"}
                  </button>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="event-reminder">Date Reminder Alert</label>
              <select id="event-reminder" className="form-control" value={form.reminder} onChange={(e) => update({ reminder: e.target.value })}>
                {REMINDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save to Schedule"}
            </button>
          </div>
        </form>
      </div>

      {pickerOpen && (
        <AssigneePicker
          users={assignees}
          selected={form.assignees}
          onClose={() => setPickerOpen(false)}
          onDone={(ids) => {
            update({ assignees: ids });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
